import { useState } from "react";

import { isTaxRegistrationNumberPrefilled, isTaxRegistrationNumberRequired } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";
import { v4 } from "uuid";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { logSalesTaxCreated } from "@/common/helpers/salesTaxTracking";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";

export interface TaxFormRow {
  key: string;
  id?: string;
  name: string;
  rate: string;
  taxRegistrationNumber: string;
}

const RATE_PPM_PER_PERCENT = 10_000;
const RATE_INPUT_PATTERN = /^\d{1,3}(\.\d{1,4})?$/;

export const parseRatePercent = (rate: string): number | null => {
  const trimmed = rate.trim();
  if (!RATE_INPUT_PATTERN.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  if (!(value > 0 && value <= 100)) {
    return null;
  }
  return value;
};

export const formatRatePpm = (ratePpm: number): string => `${ratePpm / RATE_PPM_PER_PERCENT}`;

export const normalizeTaxName = (name: string): string => name.trim().toLowerCase();

export const isTaxNameDuplicated = (name: string, stagedNames: string[], untouchedNames: string[]): boolean => {
  const normalized = normalizeTaxName(name);
  if (!normalized) {
    return false;
  }
  return (
    untouchedNames.includes(normalized) || stagedNames.filter((stagedName) => stagedName === normalized).length > 1
  );
};

export interface TaxRowErrors {
  name?: string;
  rate?: string;
  taxRegistrationNumber?: string;
}

export const getTaxRowErrorKeys = (
  row: Pick<TaxFormRow, "name" | "rate" | "taxRegistrationNumber">,
  {
    isNameDuplicated,
    isRegistrationNumberRequired,
  }: { isNameDuplicated: boolean; isRegistrationNumberRequired: boolean }
): {
  name?: "nameRequired" | "nameDuplicated";
  rate?: "rateRequired" | "rateInvalid";
  taxRegistrationNumber?: "taxIdRequired";
} => ({
  name: !row.name.trim() ? "nameRequired" : isNameDuplicated ? "nameDuplicated" : undefined,
  rate: !row.rate.trim() ? "rateRequired" : parseRatePercent(row.rate) === null ? "rateInvalid" : undefined,
  taxRegistrationNumber:
    isRegistrationNumberRequired && !row.taxRegistrationNumber.trim() ? "taxIdRequired" : undefined,
});

export const useManageCollectableTaxes = () => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { logAmplitudeEvent } = useAmplitude();
  const utils = trpc.useUtils();

  const { data: taxes } = trpc.listOrganizationTaxes.useQuery();
  const { mutateAsync: saveTaxes, isPending: isSaving } = trpc.saveOrganizationTaxes.useMutation();
  const { mutateAsync: deleteTax, isPending: isDeleting } = trpc.deleteOrganizationTax.useMutation();

  const [editedRowsByTaxId, setEditedRowsByTaxId] = useState<Record<string, TaxFormRow>>({});
  const [newRows, setNewRows] = useState<TaxFormRow[]>([]);
  const [taxIdToDelete, setTaxIdToDelete] = useState<string | null>(null);

  const taxList = taxes ?? [];
  const isRegistrationNumberRequired = isTaxRegistrationNumberRequired(organization?.country);

  const startEditTax = (taxId: string) => {
    const tax = taxList.find((item) => item.id === taxId);
    if (!tax || tax.isUsedInPurchase) {
      return;
    }
    setEditedRowsByTaxId((rows) => ({
      ...rows,
      [taxId]: {
        key: taxId,
        id: taxId,
        name: tax.name,
        rate: formatRatePpm(tax.ratePpm),
        taxRegistrationNumber: tax.taxRegistrationNumber ?? "",
      },
    }));
  };

  const cancelEditTax = (taxId: string) => {
    setEditedRowsByTaxId((rows) => {
      const { [taxId]: _removed, ...rest } = rows;
      return rest;
    });
  };

  const addNewRow = () => {
    const prefilledRegistrationNumber = isTaxRegistrationNumberPrefilled(organization?.country)
      ? (taxList.find((tax) => tax.taxRegistrationNumber)?.taxRegistrationNumber ??
        [...Object.values(editedRowsByTaxId), ...newRows].find((row) => row.taxRegistrationNumber)
          ?.taxRegistrationNumber ??
        "")
      : "";
    setNewRows((rows) => [
      ...rows,
      { key: v4(), name: "", rate: "", taxRegistrationNumber: prefilledRegistrationNumber },
    ]);
  };

  const removeNewRow = (key: string) => {
    setNewRows((rows) => rows.filter((row) => row.key !== key));
  };

  const updateRow = (key: string, patch: Partial<Pick<TaxFormRow, "name" | "rate" | "taxRegistrationNumber">>) => {
    setNewRows((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    setEditedRowsByTaxId((rows) => (rows[key] ? { ...rows, [key]: { ...rows[key], ...patch } } : rows));
  };

  const stagedRows = [...Object.values(editedRowsByTaxId), ...newRows];

  const stagedNames = stagedRows.map((row) => normalizeTaxName(row.name));
  const untouchedNames = taxList.filter((tax) => !editedRowsByTaxId[tax.id]).map((tax) => normalizeTaxName(tax.name));
  const isRowNameDuplicated = (row: TaxFormRow): boolean => isTaxNameDuplicated(row.name, stagedNames, untouchedNames);

  const errorMessages = {
    nameRequired: t("dashboard", "taxes.manage.nameRequiredError"),
    nameDuplicated: t("dashboard", "taxes.manage.nameDuplicatedError"),
    rateRequired: t("dashboard", "taxes.manage.rateRequiredError"),
    rateInvalid: t("dashboard", "taxes.manage.rateInvalidError"),
    taxIdRequired: t("dashboard", "taxes.manage.taxIdRequiredError"),
  };

  const getRowErrorKeys = (row: TaxFormRow) =>
    getTaxRowErrorKeys(row, { isNameDuplicated: isRowNameDuplicated(row), isRegistrationNumberRequired });

  const getRowErrors = (row: TaxFormRow): TaxRowErrors => {
    const keys = getRowErrorKeys(row);
    return {
      name: keys.name ? errorMessages[keys.name] : undefined,
      rate: keys.rate ? errorMessages[keys.rate] : undefined,
      taxRegistrationNumber: keys.taxRegistrationNumber ? errorMessages[keys.taxRegistrationNumber] : undefined,
    };
  };

  const isRowValid = (row: TaxFormRow): boolean => {
    const keys = getRowErrorKeys(row);
    return !keys.name && !keys.rate && !keys.taxRegistrationNumber;
  };

  const hasStagedChanges = stagedRows.length > 0;
  const canSave = hasStagedChanges && stagedRows.every(isRowValid) && !isSaving;

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    const { error } = await saveTaxes({
      taxes: stagedRows.map((row) => ({
        id: row.id,
        name: row.name.trim(),
        ratePpm: Math.round((parseRatePercent(row.rate) ?? 0) * RATE_PPM_PER_PERCENT),
        taxRegistrationNumber: row.taxRegistrationNumber.trim() || null,
      })),
    });
    if (error) {
      enqueueSnackbar(t("dashboard", "taxes.manage.saveError"), { vibe: "danger" });
      return;
    }
    for (const row of stagedRows.filter((stagedRow) => !stagedRow.id)) {
      logSalesTaxCreated({
        logAmplitudeEvent,
        organizationId: organization?.id,
        source: "settings",
        ratePpm: Math.round((parseRatePercent(row.rate) ?? 0) * RATE_PPM_PER_PERCENT),
      });
    }
    enqueueSnackbar(t("dashboard", "taxes.manage.saveSuccess"), { vibe: "positive" });
    await utils.listOrganizationTaxes.invalidate();
    setEditedRowsByTaxId({});
    setNewRows([]);
  };

  const taxToDelete = taxList.find((tax) => tax.id === taxIdToDelete) ?? null;

  const handleConfirmDelete = async () => {
    if (!taxIdToDelete) {
      return;
    }
    const { error } = await deleteTax({ taxId: taxIdToDelete });
    if (error) {
      enqueueSnackbar(t("dashboard", "taxes.manage.deleteError"), { vibe: "danger" });
    } else {
      enqueueSnackbar(t("dashboard", "taxes.manage.deleteSuccess"), { vibe: "positive" });
      cancelEditTax(taxIdToDelete);
      await utils.listOrganizationTaxes.invalidate();
    }
    setTaxIdToDelete(null);
  };

  return {
    taxList,
    editedRowsByTaxId,
    newRows,
    isRegistrationNumberRequired,
    startEditTax,
    cancelEditTax,
    addNewRow,
    removeNewRow,
    updateRow,
    getRowErrors,
    canSave,
    isSaving,
    handleSave,
    taxToDelete,
    isDeleting,
    requestDeleteTax: setTaxIdToDelete,
    cancelDeleteTax: () => setTaxIdToDelete(null),
    handleConfirmDelete,
  };
};
